import { createApi, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import baseQuery from './baseQuery';
import {
  ModalAlert,
  ModalAlertBody,
  SaveModalAlertArgs
} from '../../resources/types/modalAlertsTypes';

// Картинка модалки зберігається на бекенді окремим кроком: POST /files/persistent
// (файли звідти не чистяться, на відміну від звичайного /files, де живуть 2 тижні),
// а в самій модалці лежить лише повернутий шлях.
type FetchResult = QueryReturnValue<unknown, FetchBaseQueryError, {}>;
type FetchWithBQ = (args: string | FetchArgs) => FetchResult | PromiseLike<FetchResult>;

const uploadPhoto = async (fetchWithBQ: FetchWithBQ, file: File) => {
  const form = new FormData();
  form.append('file', file);
  const result = await fetchWithBQ({ url: '/files/persistent', method: 'POST', body: form });
  if (result.error) return { error: result.error };
  return { path: (result.data as { path: string }).path };
};

export const modalAlertsApi = createApi({
  reducerPath: 'modalAlertsApi',
  baseQuery,
  tagTypes: ['ModalAlerts'],
  endpoints: builder => ({
    getModalAlerts: builder.query<{ data: ModalAlert[] }, void>({
      query: () => '/modal/admin',
      providesTags: ['ModalAlerts']
    }),

    createModalAlert: builder.mutation<{ data: ModalAlert }, SaveModalAlertArgs>({
      async queryFn({ body, file }, _api, _extra, fetchWithBQ) {
        const payload: ModalAlertBody = { ...body };

        if (file) {
          const uploaded = await uploadPhoto(fetchWithBQ, file);
          if (uploaded.error) return { error: uploaded.error };
          payload.photo = uploaded.path;
        }

        const created = await fetchWithBQ({ url: '/modal', method: 'POST', body: payload });
        if (created.error) return { error: created.error };

        // Бекенд створює модалку завжди вимкненою. Якщо адмін одразу поставив
        // «Активна», вмикаємо другим запитом, аби не ходити в редагування.
        if (body.enabled) {
          const id = (created.data as { data: ModalAlert }).data._id;
          const enabled = await fetchWithBQ({
            url: `/modal/${id}`,
            method: 'PUT',
            body: { enabled: true }
          });
          if (enabled.error) return { error: enabled.error };
          return { data: enabled.data as { data: ModalAlert } };
        }

        return { data: created.data as { data: ModalAlert } };
      },
      invalidatesTags: ['ModalAlerts']
    }),

    updateModalAlert: builder.mutation<{ data: ModalAlert }, SaveModalAlertArgs & { id: string }>({
      async queryFn({ id, body, file }, _api, _extra, fetchWithBQ) {
        const payload: ModalAlertBody = { ...body };

        if (file) {
          const uploaded = await uploadPhoto(fetchWithBQ, file);
          if (uploaded.error) return { error: uploaded.error };
          payload.photo = uploaded.path;
        }

        const result = await fetchWithBQ({ url: `/modal/${id}`, method: 'PUT', body: payload });
        if (result.error) return { error: result.error };
        return { data: result.data as { data: ModalAlert } };
      },
      invalidatesTags: ['ModalAlerts']
    }),

    deleteModalAlert: builder.mutation<{ success: boolean }, string>({
      query: id => ({ url: `/modal/${id}`, method: 'DELETE' }),
      invalidatesTags: ['ModalAlerts']
    })
  })
});

export const {
  useGetModalAlertsQuery,
  useCreateModalAlertMutation,
  useUpdateModalAlertMutation,
  useDeleteModalAlertMutation
} = modalAlertsApi;
